/**
 * Normalizes the page list off a validation result, preferring the
 * canonical `pageNumbers` array and falling back to the legacy single
 * `pageNumber` for results written before the schema change.
 */
export const getPageNumbers = (result: {
  pageNumber?: number | null;
  pageNumbers?: number[] | null;
}): number[] => {
  if (result.pageNumbers?.length) return result.pageNumbers;
  if (result.pageNumber != null) return [result.pageNumber];
  return [];
};

/**
 * Renders a list of page numbers as a human-readable label.
 *
 * - 0 pages: empty string
 * - 1 page: "Page 5"
 * - 2–5 pages: "Pages 3, 5, 7"
 * - 6+ pages: "Pages 3–29" (uses an en dash)
 */
export const formatPageList = (pageNumbers: number[]): string => {
  if (pageNumbers.length === 0) return '';
  if (pageNumbers.length === 1) return `Page ${pageNumbers[0]}`;
  if (pageNumbers.length <= 5) return `Pages ${pageNumbers.join(', ')}`;
  const first = pageNumbers[0];
  const last = pageNumbers[pageNumbers.length - 1];
  return `Pages ${first}–${last}`;
};

/**
 * Total page count of an ingestion document, derived from its
 * `contractDocumentPages` map (`{ [contractDocumentUuid]: { startPage, endPage } }`,
 * where pages are absolute within the ingestion document PDF).
 *
 * Returns the highest `endPage` across all contract documents, or `0` when the
 * map is empty/unavailable (e.g. the document hasn't been classified yet, so
 * page ranges haven't been computed). The scalar is a loosely-typed JSON
 * object, so each value is defensively narrowed before reading `endPage`.
 */
export const getDocumentPageCount = (
  contractDocumentPages?: Record<string, unknown> | null
): number => {
  if (!contractDocumentPages) return 0;

  let maxPage = 0;
  for (const value of Object.values(contractDocumentPages)) {
    const endPage = (value as { endPage?: unknown } | null | undefined)
      ?.endPage;
    if (typeof endPage === 'number' && endPage > maxPage) {
      maxPage = endPage;
    }
  }

  return maxPage;
};

/**
 * Composes a validation result's message with its failing-page list.
 * Returns the raw message when no pages are attributed.
 *
 * Single-page documents omit the page prefix: every issue is on the only
 * page, so "Page 1 - …" is noise. Pass `totalDocumentPages` (see
 * {@link getDocumentPageCount}) to enable this; when it is exactly `1` the
 * prefix is suppressed. Omit it (or pass an unknown count of `0`) to keep the
 * prefix on every page-attributed result.
 */
export const formatValidationMessage = (
  message: string,
  pageNumbers: number[],
  totalDocumentPages?: number
): string => {
  if (pageNumbers.length === 0) return message;
  if (totalDocumentPages === 1) return message;
  return `${formatPageList(pageNumbers)} - ${message}`;
};
